import * as React from "react";

/**
 * Which requests to close a toast the toast accepts.
 *
 * The kit keeps the open state itself (controlled or not) so it can refuse two kinds:
 *
 * 1. An Escape from the page. Radix makes every open toast a dismissable layer, and
 *    the top layer takes Escape from ANYWHERE without marking the event. With a
 *    toast in the corner, the Escape an editor presses to leave a text field or drop
 *    a selection would also throw the message away. So an Escape counts only when
 *    focus is in the toast or on the stack itself (F8 puts it there), or inside
 *    another Radix layer - a dialog or popover that opened before the toast. In that
 *    last case the toast is the top layer and the dialog underneath would not hear
 *    the key at all, so the toast goes first and the next Escape reaches the dialog,
 *    exactly as plain Radix behaves. Nothing is prevented: the page still gets its
 *    own Escape.
 * 2. Any close at all, when `dismissible` is false. A toast that holds a question
 *    about somebody's work (the on-page editor's unsaved-draft offer) must not go
 *    away because of a stray swipe, Escape or click on an X: the answer has to be
 *    given. Such a toast closes only when its owner sets `open` to false.
 */

/** Other Radix layers whose Escape a toast on top of them must not swallow. */
const OTHER_LAYER = '[data-radix-popper-content-wrapper], [role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

export function useToastCloseRules(
  openProp: boolean | undefined,
  defaultOpen: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined,
  dismissible: boolean,
) {
  const [innerOpen, setInnerOpen] = React.useState(defaultOpen ?? true);
  const nodeRef = React.useRef<HTMLLIElement | null>(null);
  const ignoreClose = React.useRef(false);
  const open = openProp ?? innerOpen;

  /** Runs before Radix asks to close for an Escape; decides whether that close counts. */
  const markEscape = React.useCallback(() => {
    const active = typeof document !== "undefined" ? document.activeElement : null;
    const node = nodeRef.current;
    const inStack = !!active && !!node && (node.contains(active) || active === node.parentElement);
    const inOtherLayer = !!active && !inStack && !!active.closest(OTHER_LAYER);
    ignoreClose.current = !inStack && !inOtherLayer;
    // Scoped to this one key press. Both Radix paths ask to close synchronously
    // inside the listener, so a flag still set after it can only be stale - and a
    // stale one would swallow the next real close (the timer's, or the X).
    queueMicrotask(() => {
      ignoreClose.current = false;
    });
  }, []);

  /** Every open change goes through here: Radix's (X, swipe, Escape) and the timer's. */
  const requestOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        const refuse = ignoreClose.current || !dismissible;
        ignoreClose.current = false;
        if (refuse) return;
      }
      if (openProp === undefined) setInnerOpen(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange, dismissible],
  );

  return { open, nodeRef, markEscape, requestOpenChange };
}
